import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Chart } from 'chart.js/auto';

const primaryColor = '#2E7D32';
const textPrimaryColor = '#212529';
const textSecondaryColor = '#6c757d';
const lightGrayColor = '#f8f9fa';

const createGradient = (ctx, area) => {
    const gradient = ctx.createLinearGradient(0, area.bottom, 0, area.top);
    gradient.addColorStop(0, 'rgba(40, 167, 69, 0)');
    gradient.addColorStop(0.5, 'rgba(40, 167, 69, 0.1)');
    gradient.addColorStop(1, 'rgba(40, 167, 69, 0.3)');
    return gradient;
};

const createLineChartImage = async (expenses, formatCurrency) => {
    try {
        const monthlyExpenses = expenses.reduce((acc, expense) => {
            try {
                const date = new Date(expense.data);
                if (isNaN(date.getTime())) return acc;
                const monthYear = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
                const value = parseFloat(expense.total) || 0;
                if (!acc[monthYear]) acc[monthYear] = 0;
                acc[monthYear] += value;
            } catch (e) { }
            return acc;
        }, {});

        const sortedMonths = Object.keys(monthlyExpenses).sort();
        const lineLabels = sortedMonths.map(monthYear => {
            const [year, month] = monthYear.split('-');
            return `${month}/${year.slice(2)}`;
        });
        const lineValues = sortedMonths.map(month => monthlyExpenses[month]);

        if (lineValues.length === 0) return null;

        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 400;
        const ctx = canvas.getContext('2d');

        new Chart(ctx, {
            type: 'line',
            data: {
                labels: lineLabels,
                datasets: [
                    {
                        label: 'Total Gasto por Mês',
                        data: lineValues,
                        borderColor: primaryColor,
                        backgroundColor: createGradient(ctx, { top: 0, bottom: 400, left: 0, right: 0 }),
                        fill: true,
                        tension: 0.4,
                    },
                ],
            },
            options: {
                responsive: false,
                animation: false,
                plugins: {
                    legend: { display: false },
                    title: {
                        display: true,
                        text: 'Evolução Mensal',
                        font: { size: 16, family: 'Helvetica', weight: 'bold' },
                        color: textPrimaryColor,
                    },
                    datalabels: { display: false },
                },
                scales: {
                    y: {
                        ticks: {
                            callback: (value) => formatCurrency(value),
                        },
                    },
                },
            },
        });

        return new Promise(resolve => {
            setTimeout(() => resolve(canvas.toDataURL('image/png')), 500);
        });
    } catch (error) {
        console.error("Erro ao criar gráfico de linha:", error);
        return null;
    }
};

const createDoughnutChartImage = async (expenses, formatCurrency) => {
    try {
        const categoryExpenses = expenses.reduce((acc, expense) => {
            const category = expense.tipo_de_despesa || 'Não Categorizado';
            const value = parseFloat(expense.total) || 0;
            if (!acc[category]) acc[category] = 0;
            acc[category] += value;
            return acc;
        }, {});

        const doughnutLabels = Object.keys(categoryExpenses);
        const doughnutValues = Object.values(categoryExpenses);

        if (doughnutValues.length === 0) return null;

        const dynamicColors = doughnutLabels.map((_, index) => {
            const hue = (index * 137.508) % 360;
            return `hsl(${hue}, 70%, 50%)`;
        });

        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 400;
        const ctx = canvas.getContext('2d');

        new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: doughnutLabels,
                datasets: [
                    {
                        label: 'Despesas por Categoria',
                        data: doughnutValues,
                        backgroundColor: dynamicColors,
                        borderColor: '#ffffff',
                        borderWidth: 2,
                    },
                ],
            },
            options: {
                responsive: false,
                animation: false,
                plugins: {
                    legend: {
                        position: 'right',
                        labels: {
                            font: { size: 10, family: 'Helvetica' },
                            boxWidth: 15,
                        }
                    },
                    title: {
                        display: true,
                        text: 'Despesas por Categoria',
                        font: { size: 16, family: 'Helvetica', weight: 'bold' },
                        color: textPrimaryColor,
                    },
                    datalabels: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                const percentage = (context.raw / total) * 100;
                                return `${context.label}: ${formatCurrency(context.raw)} (${percentage.toFixed(1)}%)`;
                            },
                        },
                    },
                },
                cutout: '65%',
            },
        });

        return new Promise(resolve => {
            setTimeout(() => resolve(canvas.toDataURL('image/png')), 500);
        });
    } catch (error) {
        console.error("Erro ao criar gráfico de rosca:", error);
        return null;
    }
};

export const handleGeneratePDF = async ({
    selectedExpenses,
    formatCurrency,
    formatDate,
    logo
}) => {

    if (selectedExpenses.length === 0) {
        console.error("Nenhuma despesa selecionada.");
        return;
    }

    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;

    doc.setFont('Helvetica', 'normal');

    if (logo) {
        doc.addImage(logo, 'PNG', margin, 10, 30, 10);
    }

    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(textPrimaryColor);
    doc.text('Relatório de Despesas Gerais', pageWidth / 2, 16, { align: 'center' });

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(textSecondaryColor);
    doc.text(new Date().toLocaleDateString('pt-BR', { dateStyle: 'full' }), pageWidth - margin, 15, { align: 'right' });

    doc.setLineWidth(0.2);
    doc.line(margin, 25, pageWidth - margin, 25);

    const totalGeral = selectedExpenses.reduce((acc, exp) => acc + (parseFloat(exp.total) || 0), 0);

    doc.setFontSize(11);
    doc.setTextColor(textPrimaryColor);
    doc.setFont('Helvetica', 'bold');
    doc.text('Resumo do Relatório', margin, 35);

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Total de Itens:`, margin, 42);
    doc.text(`${selectedExpenses.length}`, margin + 35, 42);

    doc.text(`Valor Total Gasto:`, margin, 48);
    doc.setFont('Helvetica', 'bold');
    doc.text(`${formatCurrency(totalGeral)}`, margin + 35, 48);
    doc.setFont('Helvetica', 'normal');
    
    const lineChartImg = await createLineChartImage(selectedExpenses, formatCurrency);
    const doughnutChartImg = await createDoughnutChartImage(selectedExpenses, formatCurrency);

    const chartWidth = (pageWidth - margin * 3) / 2;
    const chartHeight = 70;
    const chartStartY = 55;

    if (lineChartImg) {
        doc.addImage(lineChartImg, 'PNG', margin, chartStartY, chartWidth, chartHeight);
    }

    if (doughnutChartImg) {
        const doughnutWidth = 70;
        const doughnutX = margin + chartWidth + margin + (chartWidth - doughnutWidth) / 2;
        doc.addImage(doughnutChartImg, 'PNG', doughnutX, chartStartY, doughnutWidth, chartHeight);
    }

    const tableStartY = chartStartY + chartHeight + 15;

    const tableData = selectedExpenses.map(expense => [
        formatDate(expense.data),
        expense.descricao_imovel || 'N/A',
        expense.fornecedor,
        expense.descricao,
        expense.quantidade,
        formatCurrency(expense.total)
    ]);

    tableData.push([
        { content: 'Total Geral', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency(totalGeral), styles: { halign: 'right', fontStyle: 'bold' } }
    ]);

    doc.autoTable({
        head: [['Data', 'Imóvel', 'Fornecedor', 'Descrição', 'Qtd', 'Valor']],
        body: tableData,
        startY: tableStartY,
        theme: 'striped',
        styles: {
            font: 'Helvetica',
            fontSize: 9,
            cellPadding: 3,
            overflow: 'linebreak',
            valign: 'middle',
        },
        headStyles: {
            fillColor: primaryColor,
            textColor: '#FFFFFF',
            fontStyle: 'bold',
            halign: 'left',
            fontSize: 10,
        },
        alternateRowStyles: {
            fillColor: lightGrayColor,
        },
        columnStyles: {
            4: { halign: 'center' },
            5: { halign: 'right', fontStyle: 'bold', cellWidth: 35 },
        },
        didParseCell: function (data) {
            if (data.row.index === tableData.length - 1) {
                data.cell.styles.fillColor = '#FFFFFF';
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fontSize = 10;
            }
        },
        didDrawPage: (data) => {
            doc.setFontSize(8);
            doc.setTextColor(textSecondaryColor);
            doc.text(
                'Página ' + doc.internal.getNumberOfPages(),
                pageWidth - margin,
                pageHeight - 8,
                { align: 'right' }
            );
        }
    });

    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
};